import * as React from 'react';
import { Trans } from '@lingui/react';
import { useRouteError } from 'react-router';
import { CustomerFacingError } from 'utils/ui/errors';
import { userSession } from 'state/session';
import { brandSchema } from 'data/schema';
import Icon from 'components/Icon';
import { EErrorCode } from 'config/errors-codes';

interface ErrorResponse {
  status: number;
  statusText: string;
  data?: string;
}

const Error: React.FC = () => {
  const error = useRouteError() as CustomerFacingError | ErrorResponse;

  // Check if it's an ErrorResponse (from thrown Response objects)
  const isErrorResponse = error && typeof error === 'object' && 'status' in error;

  // Get error code and message based on error type
  const errorCode = isErrorResponse ? (error as ErrorResponse).status : (error as CustomerFacingError).code;
  const errorMessage = isErrorResponse ? undefined : (error as CustomerFacingError).message;

  // AVS-4405: show the logo only when the session has a valid brand. Without one (pre-gate errors)
  // `--ex3d-logo` is undefined and the mask would render as a black box, so an aria-hidden
  // placeholder of the same size keeps the message box in place. The "no Dealer ID" stop never
  // shows a logo, whatever brand is set.
  const showLogo =
    brandSchema.safeParse(userSession().get('brand')).success && errorCode !== EErrorCode.DEALER_NO_DEALER_IDS;

  return (
    <div className="ex3d-error-container flex h-screen flex-col items-center p-8" data-testid="error-boundary">
      <div className="my-4">
        {showLogo ? (
          <Icon className="ex3d-error-logo" />
        ) : (
          <span className="ex3d-error-logo-placeholder" aria-hidden="true" />
        )}
      </div>
      <div className="ex3d-error-box my-10 w-5/6 bg-gray-100 p-10">
        <div>
          <h3 className="font-bold">Error {errorCode}</h3>
          {errorMessage && (
            <span className="mt-4 text-sm">
              <Trans id={errorMessage} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Error;
